import React, { useState, useEffect } from 'react';
import QueuedScenarioCard from './QueuedScenarioCard';
import { actionButtonTemplate } from '../theme';

function ScreenerQueue({ scenarios, onRemove, onClear, onLoad, onSave, onSubmit, canRun, loading, expandSignal }) {
  // Collapsed by default — the queue card takes up a lot of space and most
  // users run directly from the form. It only auto-expands when the user
  // explicitly stages a scenario via "+ Add to Queue" (see expandSignal below).
  const [isExpanded, setIsExpanded] = useState(false);

  // Auto-expand whenever the parent bumps expandSignal (an "+ Add to Queue"
  // click). Guard against the initial 0 so the card stays collapsed on mount
  // and after a direct run (which never bumps the signal).
  useEffect(() => {
    if (expandSignal > 0) setIsExpanded(true);
  }, [expandSignal]);

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  // Estimate how many expiration dates fall inside a scenario's backend
  // search window. The screener engine returns ~1 row per expiration per
  // ticker (it picks the single closest strike per expiration). The search
  // window is derived from days_until_exp and varies a lot by tier, so the
  // expiration count must track the tier rather than be a flat constant.
  const estimateExpirationsInWindow = (daysUntilExp) => {
    const dte = Number(daysUntilExp);
    if (!Number.isFinite(dte)) return 6; // safe fallback if field is missing

    // Mirror the backend tier windows (options_analysis.py)
    let from, to;
    if (dte >= 365) { from = 335; to = 1010; }        // LEAPS
    else if (dte >= 180) { from = 150; to = 335; }    // Medium
    else { from = dte - 30; to = dte + 30; }          // Short
    from = Math.max(0, from);

    // Overlap of [from,to] with a density band, divided by that band's spacing.
    // US-equity option calendar: weeklies near term, monthlies mid, quarterly/LEAPS far out.
    const band = (bStart, bEnd, spacing) =>
      Math.max(0, Math.min(to, bEnd) - Math.max(from, bStart)) / spacing;

    const count =
      band(0, 49, 7) +      // weeklies
      band(49, 365, 30) +   // monthlies
      band(365, 1010, 90);  // quarterly / LEAPS

    return Math.max(1, Math.round(count));
  };

  // Calculate estimated result rows based on queued scenarios.
  // Each queued scenario carries one strike moneyness; strike fan-out is
  // already encoded as separate scenarios, so summing the loop covers it.
  const estimateRows = () => {
    let total = 0;
    scenarios.forEach(scenario => {
      const tickerCount = Array.isArray(scenario.tickers)
        ? scenario.tickers.length
        : scenario.tickers.split(',').filter(t => t.trim()).length;
      total += tickerCount * estimateExpirationsInWindow(scenario.days_until_exp);
    });
    return total;
  };

  return (
    <div className="queue-section">
      {/* Collapsible Header */}
      <div
        className={`queue-header ${isExpanded ? 'expanded' : 'collapsed'}`}
        onClick={toggleExpanded}
      >
        <div className="queue-header-left">
          <span className={`chevron ${isExpanded ? 'rotate-90' : ''}`}>
            ›
          </span>
          <h3>Scenario Queue ({scenarios.length})</h3>
        </div>

        <div className="queue-header-actions" onClick={(e) => e.stopPropagation()}>
          {isExpanded && scenarios.length > 0 && (
            <button
              onClick={onClear}
              className={`${actionButtonTemplate.cssClasses.base} ${actionButtonTemplate.cssClasses.danger}`}
              title="Clear all scenarios from queue"
            >
              Clear All
            </button>
          )}
          {!isExpanded && scenarios.length > 0 && (
            <button
              onClick={onSubmit}
              className={`${actionButtonTemplate.cssClasses.base} ${actionButtonTemplate.cssClasses.primary}`}
              disabled={!canRun || loading}
            >
              {loading ? (
                <span className="spinner-small"></span>
              ) : (
                <span>Run {scenarios.length} scenario{scenarios.length > 1 ? 's' : ''} (approx. {estimateRows().toLocaleString()} rows)</span>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Collapsible Content */}
      <div className={`queue-content ${isExpanded ? 'expanded' : ''}`}>
        {scenarios.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-text">
              No scenarios in queue. Add scenarios from the form to begin.
            </div>
          </div>
        ) : (
          <>
            <div className="queue-list">
              {scenarios.map((scenario, idx) => (
                <QueuedScenarioCard
                  key={idx}
                  scenario={scenario}
                  index={idx}
                  onRemove={() => onRemove(idx)}
                  onLoad={() => onLoad(idx)}
                  onSave={onSave ? () => onSave(scenario) : null}
                  showDetails={true}
                />
              ))}
            </div>

            {/* Run Button at Bottom when Expanded */}
            <button
              type="button"
              onClick={onSubmit}
              className={`${actionButtonTemplate.cssClasses.base} ${actionButtonTemplate.cssClasses.run} ${(!canRun || loading) ? actionButtonTemplate.cssClasses.disabled : ''}`}
              disabled={!canRun || loading}
            >
              {loading ? (
                <span className="spinner"></span>
              ) : (
                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <span>Run {scenarios.length} scenario{scenarios.length > 1 ? 's' : ''} (approx. {estimateRows().toLocaleString()} rows)</span>
                </span>
              )}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default ScreenerQueue;
